import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@repo/blacksmith-ui/cn";
interface HoverTickerTextProps {
  text: string;
  className?: string;
  speedPxPerSecond?: number; // Used to derive duration from overflow distance
  paddingPx?: number; // Extra space to scroll past the end for readability
}
export function HoverTickerText(t0) {
  const $ = _c(38);
  const {
    text,
    className,
    speedPxPerSecond: t1,
    paddingPx: t2
  } = t0;
  const speedPxPerSecond = t1 === undefined ? 40 : t1;
  const paddingPx = t2 === undefined ? 20 : t2;
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [duration, setDuration] = useState(6);
  const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let t3;
  if ($[0] !== paddingPx || $[1] !== speedPxPerSecond) {
    t3 = () => {
      if (!containerRef.current || !textRef.current) {
        return;
      }
      const containerWidth = containerRef.current.offsetWidth;
      const textWidth = textRef.current.scrollWidth;
      if (textWidth > containerWidth) {
        const overflowDistance = Math.max(0, textWidth - containerWidth + paddingPx);
        setShouldScroll(true);
        setScrollDistance(overflowDistance);
        const distanceSeconds = overflowDistance / Math.max(1, speedPxPerSecond);
        const nextDuration = Math.min(14, Math.max(4, Math.round((distanceSeconds + 3) * 10) / 10));
        setDuration(nextDuration);
      } else {
        setShouldScroll(false);
        setScrollDistance(0);
      }
    };
    $[0] = paddingPx;
    $[1] = speedPxPerSecond;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const measure = t3;
  let t4;
  let t5;
  if ($[3] !== measure) {
    t4 = () => {
      if (!containerRef.current) {
        return;
      }
      let settleTimer;
      const observer = new ResizeObserver(() => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(measure, 150);
      });
      observer.observe(containerRef.current);
      requestAnimationFrame(() => measure());
      return () => {
        observer.disconnect();
        clearTimeout(settleTimer);
      };
    };
    t5 = [measure];
    $[3] = measure;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== text) {
    t6 = <span ref={textRef} aria-hidden="true" className="invisible absolute left-0 top-0 whitespace-nowrap">{text}</span>;
    $[6] = text;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const measureCopy = t6;
  let t7;
  if ($[8] !== className) {
    t7 = cn("relative truncate whitespace-nowrap", className);
    $[8] = className;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const baseClassName = t7;
  if (!shouldScroll) {
    let t8;
    if ($[10] !== baseClassName || $[11] !== measureCopy || $[12] !== text) {
      t8 = <span ref={containerRef} className={baseClassName} aria-label={text}>{measureCopy}{text}</span>;
      $[10] = baseClassName;
      $[11] = measureCopy;
      $[12] = text;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (prefersReducedMotion) {
    let t8;
    if ($[14] !== className) {
      t8 = cn("relative whitespace-nowrap truncate group-hover:overflow-x-auto group-hover:text-clip", className);
      $[14] = className;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== measureCopy || $[17] !== t8 || $[18] !== text) {
      t9 = <span ref={containerRef} className={t8} aria-label={text}>{measureCopy}{text}</span>;
      $[16] = measureCopy;
      $[17] = t8;
      $[18] = text;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    return t9;
  }
  let t8;
  if ($[20] !== className) {
    t8 = cn("ticker-container truncate whitespace-nowrap", className);
    $[20] = className;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== text) {
    t9 = <span className="group-hover:hidden">{text}</span>;
    $[22] = text;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = `${duration}s`;
  const t11 = `-${scrollDistance}px`;
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = {
      "--duration": t10,
      "--scroll-distance": t11
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = t12 as React.CSSProperties;
  let t14;
  if ($[27] !== text) {
    t14 = <span className="flex-shrink-0 inline-block w-max">{text}</span>;
    $[27] = text;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13 || $[30] !== t14) {
    t15 = <span aria-hidden="true" className="hidden group-hover:flex whitespace-nowrap [animation:ticker-scroll-cycle_var(--duration)_ease-in-out_infinite]" style={t13}>{t14}</span>;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== measureCopy || $[33] !== t15 || $[34] !== t8 || $[35] !== t9 || $[36] !== text) {
    t16 = <span ref={containerRef} className={t8} aria-label={text}>{measureCopy}{t9}{t15}</span>;
    $[32] = measureCopy;
    $[33] = t15;
    $[34] = t8;
    $[35] = t9;
    $[36] = text;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}